import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useParams } from 'react-router-dom';
import '../css/VendorPurchaseDetails.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AuthContext } from '../context/AuthContext';
import { vendorsAPI, returnsAPI, getCompanyAccounts, getRawMaterials } from '../utils/api';
import SearchableDropdown from '../components/SearchableDropdown';

const VendorTransactions = ({
    vendorName,
    transactions: propTransactions,
    onFetch,
    filterType,
    setFilterType,
    fromDate: propFromDate,
    toDate: propToDate,
    setFromDate: propSetFromDate,
    setToDate: propSetToDate
}) => {
    const { user } = useContext(AuthContext);
    const role = user?.role?.toLowerCase();

    /* ===================== GET vendorId FROM URL ===================== */
    const { vendorId } = useParams();

    /* ===================== STATE ===================== */
    const [internalTransactions, setInternalTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Prioritize props > Internal
    const transactions = propTransactions || internalTransactions;

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Selection state
    const [selectedIds, setSelectedIds] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingTx, setEditingTx] = useState(null);
    const [editForm, setEditForm] = useState({
        transaction_date: '',
        bill_number: '',
        transaction_type: '',
        product: '',
        quantity: '',
        unit_price: '',
        discount: '',
        with_gst: false,
        total_amount: '',
        amount_paid: '',
        account_paid: ''
    });
    const [companyAccounts, setCompanyAccounts] = useState([]);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [productFilter, setProductFilter] = useState('');

    /* ===================== FORMAT HELPERS ===================== */
    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-IN');
    };

    const formatCurrency = (amount) => {
        return `₹${Number(amount || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    /* ===================== FETCH TRANSACTIONS ===================== */
    const fetchTransactions = async () => {
        if (onFetch) {
            onFetch();
            return;
        }

        if (propTransactions) return;

        if (!vendorId) {
            setError('No vendor selected');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await vendorsAPI.getTransactions(vendorId);

            if (response.data?.success) {
                const formatted = (response.data.transactions || []).map(txn => ({
                    id: txn.id,
                    transaction_date: txn.transaction_date,
                    bill_number: txn.reference_number || txn.bill_number || '-',
                    transaction_type:
                        txn.transaction_type === 'cash' ? 'purchase' : txn.transaction_type,
                    product: txn.product || '-',
                    quantity: Number(txn.quantity) || 0,
                    purchase_price: Number(txn.unit_price) || 0,
                    discount: Number(txn.discount) || 0,
                    with_gst: Boolean(txn.with_gst),
                    total_amount: Number(txn.total_amount || txn.amount) || 0,
                    paid_amount: Number(txn.amount_paid) || 0,
                    due_balance:
                        (Number(txn.total_amount || txn.amount) || 0) -
                        (Number(txn.amount_paid) || 0),
                    account_paid: txn.account_paid || '',
                    items: txn.items || []
                }));

                setInternalTransactions(formatted);
            } else {
                setInternalTransactions([]);
                setError('No transactions found');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load transactions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!propTransactions) {
            fetchTransactions();
        }
        fetchCompanyAccounts();
        fetchRawMaterialsList();
    }, [vendorId, propTransactions]);

    const fetchRawMaterialsList = async () => {
        try {
            const data = await getRawMaterials();
            setRawMaterials(Array.isArray(data) ? data.map(m => m.name || m.current_name || m.product_name || m.id) : []);
        } catch (error) {
            // console.error('Error fetching raw materials:', error);
        }
    };

    const fetchCompanyAccounts = async () => {
        try {
            const accounts = await getCompanyAccounts();
            setCompanyAccounts(accounts || []);
        } catch (error) {
            // console.error('Error fetching company accounts:', error);
        }
    };

    /* ===================== FILTER LOGIC ===================== */
    const filteredTransactions = useMemo(() => {
        return transactions.filter(txn => {
            // Date Filter
            const txnDate = new Date(txn.transaction_date);
            const fDate = propFromDate || fromDate;
            const tDate = propToDate || toDate;
            if (fDate && txnDate < new Date(fDate)) return false;
            if (tDate && txnDate > new Date(tDate)) return false;

            // Type Filter
            const typeValue = filterType || 'all';
            let matchesType = true;
            if (typeValue !== 'all') {
                if (typeValue === 'PURCHASE') matchesType = txn.transaction_type === 'purchase';
                else if (typeValue === 'PAYMENT') matchesType = txn.transaction_type === 'payment';
                else if (typeValue === 'RETURN') matchesType = txn.transaction_type === 'return';
                else if (typeValue === 'OPENING') matchesType = txn.transaction_type === 'opening';
                if (!matchesType) return false;
            }

            // Product Filter (Exact Match)
            if (productFilter) {
                const searchLower = productFilter.toLowerCase();
                const matchesParent = txn.product?.toLowerCase() === searchLower;
                const matchesItems = txn.items?.some(item => (item.product_name || item.product)?.toLowerCase() === searchLower);

                if (!matchesParent && !matchesItems) {
                    return false;
                }
            }

            return true;
        });
    }, [transactions, fromDate, toDate, propFromDate, propToDate, filterType, productFilter]);

    /* ===================== RESET PAGE ON FILTER CHANGE ===================== */
    useEffect(() => {
        setCurrentPage(1);
    }, [fromDate, toDate, propFromDate, propToDate, rowsPerPage, filterType]);

    /* ===================== PAGINATION ===================== */
    const paginatedTransactions = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return filteredTransactions.slice(start, start + rowsPerPage);
    }, [filteredTransactions, currentPage, rowsPerPage]);

    const totalPages = Math.ceil(filteredTransactions.length / rowsPerPage);

    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;
        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            if (currentPage <= 3) {
                pageNumbers.push(1, 2, 3, 4, '...', totalPages);
            } else if (currentPage >= totalPages - 2) {
                pageNumbers.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pageNumbers.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
            }
        }
        return pageNumbers;
    };

    /* ===================== SELECTION HANDLERS ===================== */
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(paginatedTransactions.map(t => t.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectRow = (id) => {
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    const handleBulkDelete = async () => {
        if (!selectedIds.length) return;

        if (window.confirm(`Are you sure you want to delete ${selectedIds.length} selected transaction(s)?`)) {
            try {
                setLoading(true);
                for (const id of selectedIds) {
                    await vendorsAPI.deleteTransaction(id);
                }
                setSelectedIds([]);
                fetchTransactions();
                alert('Transactions deleted successfully');
            } catch (err) {
                // console.error('Bulk delete error:', err);
                alert('Failed to delete some transactions');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleEditClick = () => {
        if (selectedIds.length !== 1) return;
        const tx = transactions.find(t => t.id === selectedIds[0]);
        if (tx) {
            setEditingTx(tx);
            setEditForm({
                transaction_date: tx.transaction_date ? new Date(tx.transaction_date).toISOString().split('T')[0] : '',
                bill_number: tx.bill_number || tx.reference_number || '',
                transaction_type: tx.transaction_type || '',
                product: tx.product || '',
                quantity: tx.quantity || 0,
                unit_price: tx.purchase_price || tx.unit_price || 0,
                discount: tx.discount || 0,
                with_gst: tx.with_gst || false,
                total_amount: tx.total_amount || tx.amount || 0,
                amount_paid: tx.paid_amount || tx.amount_paid || 0,
                account_paid: tx.account_paid || '',
                items: (tx.items && tx.items.length > 0)
                    ? tx.items.map(i => ({
                        product: i.product_name || i.product || '',
                        quantity: Number(i.quantity) || 0,
                        unit_price: Number(i.unit_price) || 0,
                        with_gst: Boolean(i.with_gst)
                    }))
                    : [{
                        product: tx.product || '',
                        quantity: Number(tx.quantity || tx.qty) || 0,
                        unit_price: Number(tx.purchase_price || tx.unit_price) || 0,
                        with_gst: Boolean(tx.with_gst)
                    }]
            });
            setShowEditModal(true);
        }
    };

    const handleSaveEdit = async () => {
        try {
            setLoading(true);
            if (editingTx.transaction_type === 'opening') {
                await vendorsAPI.updateOpeningBalancePaid(vendorId, {
                    ...editForm,
                });
            } else {
                if (String(editingTx.id).startsWith('RET-')) {
                    // It's a return transaction
                    const returnId = String(editingTx.id).replace('RET-', '');
                    await returnsAPI.update(returnId, {
                        ...editForm,
                    });
                } else {
                    // 1. Update the original transaction
                    const transactionId = String(editingTx.id).replace(/^tx-/i, '');
                    await vendorsAPI.updateTransaction(
                        transactionId,
                        {
                            ...editForm,
                        }
                    );
                }
            }
            setShowEditModal(false);
            setSelectedIds([]);

            // Refresh parent or fetch localized based on available methods
            if (onFetch) {
                onFetch();
            } else {
                fetchTransactions();
            }

            alert('Transaction updated successfully');
        } catch (err) {
            // console.error('Update error:', err);
            alert('Failed to update transaction');
        } finally {
            setLoading(false);
        }
    };

    const recalcEditTotal = (form) => {
        let totalSubtotal = 0;
        let totalGST = 0;

        if (form.items && form.items.length > 0) {
            form.items.forEach(item => {
                const qty = Number(item.quantity) || 0;
                const price = Number(item.unit_price) || 0;
                const itemAmount = qty * price;
                totalSubtotal += itemAmount;

                if (item.with_gst) {
                    totalGST += itemAmount * 0.18;
                }
            });
        }

        const discount = Number(form.discount) || 0;
        let grandTotal = (totalSubtotal + totalGST) - discount;
        if (grandTotal < 0) grandTotal = 0;

        form.total_amount = Math.round(grandTotal);
        return form;
    };

    const handleItemChange = (index, field, value) => {
        setEditForm(prev => {
            const newItems = [...(prev.items || [])];
            newItems[index] = { ...newItems[index], [field]: value };
            return recalcEditTotal({ ...prev, items: newItems });
        });
    };

    const addItem = () => {
        setEditForm(prev => ({
            ...prev,
            items: [...(prev.items || []), { product: '', quantity: '', unit_price: '', discount: '', with_gst: false }]
        }));
    };

    const removeItem = (index) => {
        setEditForm(prev => {
            const newItems = (prev.items || []).filter((_, i) => i !== index);
            return recalcEditTotal({
                ...prev,
                items: newItems.length > 0 ? newItems : [{ product: '', quantity: '', unit_price: '', discount: '', with_gst: false }]
            });
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm(prev => {
            const newForm = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };

            if (name === 'discount') {
                return recalcEditTotal(newForm);
            }

            return newForm;
        });
    };

    /* ===================== PDF HELPERS ===================== */
    const formatCurrencyWithoutSymbol = (amount) => {
        if (!amount) return '0.00';
        const num = parseFloat(amount);
        return num.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatDateDDMMYYYY = (date) => {
        const d = new Date(date);
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    };

    /* ===================== PDF EXPORT ===================== */
    const exportToPDF = () => {
        if (!filteredTransactions.length) {
            alert('No transactions to export!');
            return;
        }

        try {
            const doc = new jsPDF('landscape');

            // ---------- HEADER ----------
            doc.setFontSize(24);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 128);
            doc.text('OMEGA', 14, 20);

            doc.setFontSize(16);
            doc.setTextColor(0, 0, 0);
            doc.text('Vendor Purchase Transaction Details', 14, 30);

            doc.setDrawColor(0, 0, 128);
            doc.line(14, 35, 280, 35);

            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text('Vendor:', 14, 45);
            doc.setFont('helvetica', 'normal');
            doc.text(vendorName || 'N/A', 30, 45);

            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text('Vendor ID:', 90, 45);
            doc.setFont('helvetica', 'normal');
            doc.text(vendorId || 'N/A', 115, 45);

            doc.setFont('helvetica', 'bold');
            doc.text('Report Date:', 200, 45);
            doc.setFont('helvetica', 'normal');
            doc.text(formatDateDDMMYYYY(new Date()), 225, 45);

            let yPos = 55;
            const fDate = propFromDate || fromDate;
            const tDate = propToDate || toDate;
            if (fDate || tDate) {
                doc.setFont('helvetica', 'bold');
                doc.text('Period:', 14, yPos);
                doc.setFont('helvetica', 'normal');
                doc.text(`${fDate || 'Start'} to ${tDate || 'End'}`, 30, yPos);
                yPos += 10;
            }

            // ---------- TABLE DATA ----------
            const tableData = filteredTransactions.map(txn => {
                const hasItems = txn.items && txn.items.length > 0;

                let productText = txn.product || '-';
                let quantityText = (txn.transaction_type === 'opening' ? '-' : (txn.quantity || '-')).toString();
                let unitPriceText = txn.transaction_type === 'opening' ? '-' : formatCurrencyWithoutSymbol(txn.purchase_price || txn.unit_price || 0);

                if (hasItems) {
                    // Filter items if product filter is active (exact match)
                    const displayItems = productFilter
                        ? txn.items.filter(item => (item.product_name || item.product)?.toLowerCase() === productFilter.toLowerCase())
                        : txn.items;

                    productText = displayItems.map(item => `• ${item.product_name || item.product}`).join('\n');
                    quantityText = displayItems.map(item => item.quantity).join('\n');
                    unitPriceText = displayItems.map(item => formatCurrencyWithoutSymbol(item.unit_price || 0)).join('\n');
                }

                return [
                    formatDate(txn.transaction_date),
                    txn.bill_number || txn.reference_number || '-',
                    txn.transaction_type?.toUpperCase() || '-',
                    productText,
                    quantityText,
                    unitPriceText,
                    txn.transaction_type === 'opening' ? '-' : formatCurrencyWithoutSymbol(txn.discount || 0),
                    txn.transaction_type === 'opening' ? '-' : (txn.with_gst ? 'Yes' : 'No'),
                    formatCurrencyWithoutSymbol(txn.total_amount || 0),
                    formatCurrencyWithoutSymbol(txn.paid_amount || txn.amount_paid || 0),
                    txn.account_paid || '-',
                    txn.transaction_type === 'opening' ? '-' : formatCurrencyWithoutSymbol(txn.balance !== undefined ? txn.balance : txn.due_balance || 0)
                ];
            });

            const totals = {
                total_amount: filteredTransactions.reduce((s, t) => s + (+t.total_amount || 0), 0),
                amount_paid: filteredTransactions.reduce((s, t) => s + (+(t.paid_amount || t.amount_paid) || 0), 0),
                balance: filteredTransactions.reduce((s, t) => s + (+t.due_balance || 0), 0)
            };

            autoTable(doc, {
                startY: yPos + 5,
                head: [[
                    'Date', 'Bill No', 'Type', 'Product', 'Qty',
                    'Unit Price', 'Discount', 'GST',
                    'Total Amt', 'Paid', 'Account Paid', 'Balance'
                ]],
                body: tableData,
                styles: { fontSize: 8, cellPadding: 3 },
                headStyles: { fillColor: [0, 0, 128], textColor: 255 },
                columnStyles: {
                    0: { cellWidth: 20 },
                    1: { cellWidth: 24 },
                    2: { cellWidth: 16 },
                    3: { cellWidth: 32 },
                    4: { cellWidth: 12, halign: 'right' },
                    5: { cellWidth: 20, halign: 'right' },
                    6: { cellWidth: 16, halign: 'right' },
                    7: { cellWidth: 12 },
                    8: { cellWidth: 22, halign: 'right' },
                    9: { cellWidth: 22, halign: 'right' },
                    10: { cellWidth: 24 },
                    11: { cellWidth: 24, halign: 'right' }
                },
                margin: { left: 14, right: 14 }
            });

            // ---------- SUMMARY ----------
            const finalY = doc.lastAutoTable.finalY;
            const summaryY = finalY + 10;

            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`Total Txn: ${filteredTransactions.length}`, 40, summaryY);
            doc.text(`Total Amt: ${formatCurrencyWithoutSymbol(totals.total_amount)}`, 90, summaryY);
            doc.text(`Paid: ${formatCurrencyWithoutSymbol(totals.amount_paid)}`, 150, summaryY);
            doc.text(`Balance: ${formatCurrencyWithoutSymbol(totals.balance)}`, 210, summaryY);

            // ---------- FOOTER ----------
            doc.setFontSize(9);
            doc.setFont('helvetica', 'italic');
            doc.text(
                'End of Report • Generated by Omega Inventory System',
                14,
                summaryY + 12
            );

            const dateStr = formatDateDDMMYYYY(new Date());
            const sanitizedName = (vendorName || 'Vendor').replace(/[^a-zA-Z0-9]/g, '_');
            doc.save(`Purchase_Transaction_${sanitizedName}_${dateStr}.pdf`);

        } catch (error) {
            // console.error('PDF Error:', error);
            alert('Failed to generate PDF');
        }
    };

    /* ===================== RENDER ===================== */
    return (
        <div className="transactions-table-container">
            <div className="table-header d-flex justify-content-between align-items-center">
                <h3>Purchase Transactions</h3>
                <span>
                    {loading ? 'Loading...' : `Showing ${paginatedTransactions.length} of ${filteredTransactions.length} entries`}
                </span>
            </div>

            {/* FILTERS */}
            <div className="table-filters">
                <div className="filter-card">
                    <div className="filter-item">
                        <label>From Date</label>
                        <input
                            type="date"
                            className="filter-input"
                            value={propFromDate !== undefined ? propFromDate : fromDate}
                            onChange={(e) => (propSetFromDate || setFromDate)(e.target.value)}
                        />
                    </div>

                    <div className="filter-item">
                        <label>To Date</label>
                        <input
                            type="date"
                            className="filter-input"
                            value={propToDate !== undefined ? propToDate : toDate}
                            onChange={(e) => (propSetToDate || setToDate)(e.target.value)}
                        />
                    </div>

                    <div className="filter-item">
                        <label>Transaction Type</label>
                        <select
                            className="filter-input"
                            value={filterType || 'all'}
                            onChange={e => setFilterType && setFilterType(e.target.value)}
                        >
                            <option value="all">All Types</option>
                            <option value="PURCHASE">Purchase Bill</option>
                            <option value="PAYMENT">Payment Made</option>
                            <option value="RETURN">Return</option>
                            <option value="OPENING">Opening Balance</option>
                        </select>
                    </div>

                    <div className="filter-item">
                        <label>Product</label>
                        <select
                            className="filter-input"
                            value={productFilter}
                            onChange={(e) => setProductFilter(e.target.value)}
                        >
                            <option value="">All Products</option>
                            {rawMaterials.map((m, i) => (
                                <option key={m.id || i} value={m.name || m.material_name}>
                                    {m.name || m.material_name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-item">
                        <label>&nbsp;</label>
                        <button
                            className="btn btn-fetch"
                            onClick={fetchTransactions}
                            disabled={loading}
                        >
                            {loading ? 'Fetching...' : 'Fetch'}
                        </button>
                    </div>

                    <div className="filter-actions">
                        <button
                            className="btn btn-clear"
                            onClick={() => {
                                if (propSetFromDate) propSetFromDate('');
                                else setFromDate('');

                                if (propSetToDate) propSetToDate('');
                                else setToDate('');

                                if (setFilterType) setFilterType('all');
                            }}
                        >
                            Clear
                        </button>
                        <button
                            className="btn btn-export"
                            onClick={exportToPDF}
                            disabled={filteredTransactions.length === 0}
                        >
                            Export PDF
                        </button>
                    </div>
                </div>

                {role !== 'normal_user' && (
                    <div className="action-bar mb-3 d-flex gap-2">
                        <button
                            className="btn btn-primary"
                            disabled={selectedIds.length !== 1}
                            onClick={handleEditClick}
                        >
                            <i className="bi bi-pencil"></i> Edit Transaction
                        </button>
                        <button
                            className="btn btn-danger"
                            disabled={selectedIds.length === 0}
                            onClick={handleBulkDelete}
                        >
                            <i className="bi bi-trash"></i> Delete Selected ({selectedIds.length})
                        </button>
                    </div>
                )}
            </div>

            {error && !loading && <div className="alert alert-warning">{error}</div>}

            <div className="table-responsive">
                <table className="transactions-table table table-striped table-bordered table-hover">
                    <thead className="table-dark">
                        <tr>
                            <th style={{ width: '40px' }} className="text-center">
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={paginatedTransactions.length > 0 && selectedIds.length === paginatedTransactions.length}
                                />
                            </th>
                            <th>Date</th>
                            <th>Bill No</th>
                            <th>Type</th>
                            <th>Product</th>
                            <th className="text-end">Qty</th>
                            <th className="text-end">Unit Price</th>
                            <th className="text-end">Discount</th>
                            <th className="text-center">GST</th>
                            <th className="text-end">Total</th>
                            <th className="text-end">Paid</th>
                            <th>Account Paid</th>
                            <th className="text-end">Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="13" className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <div className="mt-2">Loading transactions...</div>
                                </td>
                            </tr>
                        ) : filteredTransactions.length === 0 ? (
                            <tr>
                                <td colSpan="13" className="text-center py-4">
                                    No transactions found
                                </td>
                            </tr>
                        ) : paginatedTransactions.map(txn => (
                            <tr key={txn.id} className={selectedIds.includes(txn.id) ? 'table-primary' : ''}>
                                <td className="text-center">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(txn.id)}
                                        onChange={() => handleSelectRow(txn.id)}
                                    />
                                </td>
                                <td>{formatDate(txn.transaction_date)}</td>
                                <td>{txn.bill_number || txn.reference_number}</td>
                                <td>
                                    <span className={`badge ${txn.transaction_type === 'purchase' ? 'bg-success' :
                                        txn.transaction_type === 'return' ? 'bg-danger' :
                                            txn.transaction_type === 'payment' ? 'bg-info' :
                                                txn.transaction_type === 'opening' ? 'bg-warning text-dark' : 'bg-secondary'
                                        }`}>
                                        {txn.transaction_type?.toUpperCase()}
                                    </span>
                                </td>
                                <td>
                                    {txn.items && txn.items.length > 0 ? (
                                        <div style={{ fontSize: '0.9em' }}>
                                            {(productFilter
                                                ? txn.items.filter(item => (item.product_name || item.product)?.toLowerCase() === productFilter.toLowerCase())
                                                : txn.items
                                            ).map((item, i) => (
                                                <div key={i} style={{ marginBottom: '4px', whiteSpace: 'nowrap' }}>
                                                    • {item.product_name || item.product}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        txn.product
                                    )}
                                </td>
                                <td className="text-end">
                                    {txn.items && txn.items.length > 0 ? (
                                        <div style={{ fontSize: '0.9em' }}>
                                            {(productFilter
                                                ? txn.items.filter(item => (item.product_name || item.product)?.toLowerCase() === productFilter.toLowerCase())
                                                : txn.items
                                            ).map((item, i) => (
                                                <div key={i} style={{ marginBottom: '4px' }}>
                                                    {item.quantity}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        txn.transaction_type === 'opening' ? '-' : txn.quantity
                                    )}
                                </td>
                                <td className="text-end">
                                    {txn.items && txn.items.length > 0 ? (
                                        <div style={{ fontSize: '0.9em' }}>
                                            {(productFilter
                                                ? txn.items.filter(item => (item.product_name || item.product)?.toLowerCase() === productFilter.toLowerCase())
                                                : txn.items
                                            ).map((item, i) => (
                                                <div key={i} style={{ marginBottom: '4px' }}>
                                                    {formatCurrency(item.unit_price)}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        txn.transaction_type === 'opening' ? '-' : formatCurrency(txn.purchase_price)
                                    )}
                                </td>
                                <td className="text-end">{txn.transaction_type === 'opening' ? '-' : formatCurrency(txn.discount)}</td>
                                <td className="text-center">
                                    {txn.transaction_type === 'opening' ? '-' : (txn.with_gst ?
                                        <span className="badge bg-success">Yes</span> :
                                        <span className="badge bg-secondary">No</span>)}
                                </td>
                                <td className="text-end fw-bold">{formatCurrency(txn.total_amount)}</td>
                                <td className="text-end text-success">{formatCurrency(txn.paid_amount || txn.amount_paid)}</td>
                                <td>{txn.account_paid || '-'}</td>
                                <td className={`text-end fw-bold ${parseFloat(txn.due_balance || 0) > 0 ? 'text-danger' : 'text-success'}`}>
                                    {txn.transaction_type === 'opening' ? '-' : formatCurrency(txn.balance !== undefined ? txn.balance : txn.due_balance)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination Section */}
            {filteredTransactions.length > 0 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 p-3 border-top">
                    <div className="text-muted mb-2 mb-md-0">
                        Showing <strong>{((currentPage - 1) * rowsPerPage) + 1}</strong> to <strong>{Math.min(currentPage * rowsPerPage, filteredTransactions.length)}</strong> of <strong>{filteredTransactions.length}</strong> entries
                        {totalPages > 1 && <span className="ms-2">(Page {currentPage} of {totalPages})</span>}
                    </div>

                    {totalPages > 1 && (
                        <nav aria-label="Transaction pagination" className="mb-2 mb-md-0">
                            <ul className="pagination mb-0">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>&laquo;</button>
                                </li>
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>&lsaquo;</button>
                                </li>

                                {getPageNumbers().map((page, index) => (
                                    <li key={index} className={`page-item ${currentPage === page ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => typeof page === 'number' && setCurrentPage(page)}>{page}</button>
                                    </li>
                                ))}

                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>&rsaquo;</button>
                                </li>
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>&raquo;</button>
                                </li>
                            </ul>
                        </nav>
                    )}

                    <div className="d-flex align-items-center">
                        <span className="text-muted me-2">Show:</span>
                        <select
                            className="form-select form-select-sm"
                            value={rowsPerPage}
                            onChange={(e) => {
                                setRowsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            style={{ width: 'auto' }}
                        >
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                        </select>
                        <span className="text-muted ms-2">per page</span>
                    </div>
                </div>
            )}

            {/* Edit Transaction Modal */}
            {showEditModal && editingTx && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Transaction</h5>
                                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Date</label>
                                        <input type="date" className="form-control" name="transaction_date" value={editForm.transaction_date} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Bill No</label>
                                        <input type="text" className="form-control" name="bill_number" value={editForm.bill_number} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Type</label>
                                        <input type="text" className="form-control" name="transaction_type" value={editForm.transaction_type} onChange={handleChange} />
                                    </div>
                                    {/* ITEMS LIST */}
                                    <div className="col-12 mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <label className="form-label fw-bold mb-0">Items</label>
                                        </div>

                                        <div className="items-section">
                                            {/* Table Header */}
                                            <div className="items-header" style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'minmax(150px, 3fr) 80px 100px 100px 50px 40px',
                                                gap: '10px',
                                                marginBottom: '5px',
                                                padding: '0 10px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                color: '#555'
                                            }}>
                                                <div>Product</div>
                                                <div>Qty</div>
                                                <div>Price (₹)</div>
                                                <div>Total (₹)</div>
                                                <div style={{ textAlign: 'center' }}>GST</div>
                                                <div></div>
                                            </div>

                                            {(editForm.items || []).map((item, index) => (
                                                <div key={index} className="item-row" style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'minmax(150px, 3fr) 80px 100px 100px 50px 40px',
                                                    gap: '10px',
                                                    alignItems: 'center',
                                                    marginBottom: '8px',
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #eee',
                                                    padding: '8px 10px',
                                                    borderRadius: '6px'
                                                }}>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <SearchableDropdown
                                                            options={rawMaterials}
                                                            value={item.product || ''}
                                                            onChange={(val) => handleItemChange(index, 'product', val)}
                                                            placeholder="Select Material"
                                                        />
                                                    </div>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <input type="text" className="form-control form-control-sm" value={item.quantity || ''} min="1" onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} style={{ margin: 0 }} />
                                                    </div>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <input type="text" className="form-control form-control-sm" value={item.unit_price || ''} min="0" step="0.01" onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)} style={{ margin: 0 }} />
                                                    </div>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <input type="text" className="form-control form-control-sm bg-light fw-bold" value={((Number(item.quantity) || 0) * (Number(item.unit_price) || 0)).toFixed(2)} readOnly style={{ margin: 0 }} />
                                                    </div>
                                                    <div style={{ marginBottom: 0, textAlign: 'center' }}>
                                                        <input type="checkbox" checked={item.with_gst || false} onChange={(e) => handleItemChange(index, 'with_gst', e.target.checked)} style={{ transform: 'scale(1.2)', cursor: 'pointer' }} />
                                                    </div>
                                                    <div style={{ marginBottom: 0, textAlign: 'center' }}>
                                                        <button type="button" className="btn btn-sm text-danger p-0 border-0 bg-transparent fs-5" onClick={() => removeItem(index)} title="Remove Item">×</button>
                                                    </div>
                                                </div>
                                            ))}
                                            <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={addItem}>+ Add Another Item</button>
                                        </div>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Global Discount (₹)</label>
                                        <input type="text" className="form-control" name="discount" value={editForm.discount} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label text-success fw-bold">Grand Total (₹)</label>
                                        <input type="text" className="form-control fw-bold text-success" name="total_amount" value={editForm.total_amount} readOnly />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={handleSaveEdit} disabled={loading}>
                                    {loading ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VendorTransactions;
